type ContextSyncEventType = 'TEAM_CONTEXT_SWITCHED' | 'PLATFORM_CONTEXT_SWITCHED'

export interface ContextSyncMessage {
  type: ContextSyncEventType
  targetPath: string
  timestamp: number
  sourceTabId: string
  teamId?: number
  forceReload?: boolean
}

const CONTEXT_SYNC_CHANNEL_NAME = 'glows-context-sync'
const CONTEXT_SYNC_STORAGE_KEY = 'glows_context_sync_event'
const CONTEXT_SYNC_NAVIGATION_FLAG = '__context_sync_navigation__'

const tabId = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
let contextChannel: BroadcastChannel | null = null

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function createMessage(type: ContextSyncEventType, targetPath: string): ContextSyncMessage {
  return {
    type,
    targetPath,
    timestamp: Date.now(),
    sourceTabId: tabId,
  }
}

function isValidMessage(value: unknown): value is ContextSyncMessage {
  if (!value || typeof value !== 'object') return false

  const message = value as Partial<ContextSyncMessage>
  const hasValidBase =
    (message.type === 'TEAM_CONTEXT_SWITCHED' || message.type === 'PLATFORM_CONTEXT_SWITCHED') &&
    typeof message.targetPath === 'string' &&
    typeof message.timestamp === 'number' &&
    typeof message.sourceTabId === 'string'

  if (!hasValidBase) return false
  if (message.teamId !== undefined && typeof message.teamId !== 'number') return false
  if (message.forceReload !== undefined && typeof message.forceReload !== 'boolean') return false
  return true
}

function getChannel(): BroadcastChannel | null {
  if (!isBrowser() || !('BroadcastChannel' in window)) return null

  if (!contextChannel) {
    contextChannel = new BroadcastChannel(CONTEXT_SYNC_CHANNEL_NAME)
  }

  return contextChannel
}

function publish(message: ContextSyncMessage): void {
  const channel = getChannel()
  if (channel) {
    channel.postMessage(message)
    return
  }

  if (!isBrowser()) return

  try {
    localStorage.setItem(CONTEXT_SYNC_STORAGE_KEY, JSON.stringify(message))
  } catch (error) {
    console.error('Failed to write context sync event:', error)
  }
}

export function broadcastTeamContextSwitched(
  targetPath: string,
  teamId: number,
  forceReload: boolean = true,
): void {
  publish({
    ...createMessage('TEAM_CONTEXT_SWITCHED', targetPath),
    teamId,
    forceReload,
  })
}

export function broadcastPlatformContextSwitched(targetPath: string): void {
  publish(createMessage('PLATFORM_CONTEXT_SWITCHED', targetPath))
}

export function subscribeContextSync(onMessage: (message: ContextSyncMessage) => void): () => void {
  if (!isBrowser()) return () => {}

  const channel = getChannel()

  if (channel) {
    const handleChannelMessage = (event: MessageEvent) => {
      const data = event.data
      if (!isValidMessage(data)) return
      if (data.sourceTabId === tabId) return
      onMessage(data)
    }

    channel.addEventListener('message', handleChannelMessage)

    return () => {
      channel.removeEventListener('message', handleChannelMessage)
    }
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== CONTEXT_SYNC_STORAGE_KEY || !event.newValue) return

    try {
      const data = JSON.parse(event.newValue)
      if (!isValidMessage(data)) return
      if (data.sourceTabId === tabId) return
      onMessage(data)
    } catch (error) {
      console.error('Failed to parse context sync event:', error)
    }
  }

  window.addEventListener('storage', handleStorage)
  return () => {
    window.removeEventListener('storage', handleStorage)
  }
}

export function markContextSyncNavigation(): void {
  if (!isBrowser()) return
  sessionStorage.setItem(CONTEXT_SYNC_NAVIGATION_FLAG, '1')
}

export function consumeContextSyncNavigationFlag(): boolean {
  if (!isBrowser()) return false
  const isSyncNavigation = sessionStorage.getItem(CONTEXT_SYNC_NAVIGATION_FLAG) === '1'
  if (isSyncNavigation) {
    sessionStorage.removeItem(CONTEXT_SYNC_NAVIGATION_FLAG)
  }
  return isSyncNavigation
}
